import { db } from '@/db/database'
import type { Habit, HabitCheckin } from '@/types'
import { toLocalDateString } from '@/utils/date'
import { createId } from '@/utils/id'

export interface HabitDraft {
  id?: string
  name: string
  icon: string
  color: string
  createdOn?: string
}

export const habitRepository = {
  async list(includeArchived = false): Promise<Habit[]> {
    const items = await db.habits.toArray()
    return items
      .filter((item) => includeArchived || !item.archivedAt)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  },

  async save(draft: HabitDraft): Promise<Habit> {
    const existing = draft.id ? await db.habits.get(draft.id) : undefined
    const now = new Date().toISOString()
    const habit: Habit = {
      id: existing?.id || createId(),
      name: draft.name.trim(),
      icon: draft.icon,
      color: draft.color,
      createdOn: existing?.createdOn || draft.createdOn || toLocalDateString(),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      archivedAt: existing?.archivedAt || null
    }
    await db.habits.put(habit)
    return habit
  },

  async archive(id: string, archived: boolean): Promise<void> {
    const now = new Date().toISOString()
    await db.habits.update(id, { archivedAt: archived ? now : null, updatedAt: now })
  },

  async listCheckins(month?: string): Promise<HabitCheckin[]> {
    let items = (await db.habitCheckins.toArray()).filter((item) => !item.deletedAt)
    if (month) items = items.filter((item) => item.occurredOn.startsWith(month))
    return items.sort((a, b) => b.occurredOn.localeCompare(a.occurredOn))
  },

  async toggleCheckin(habitId: string, occurredOn = toLocalDateString()): Promise<boolean> {
    const now = new Date().toISOString()
    const existing = await db.habitCheckins.where('[habitId+occurredOn]').equals([habitId, occurredOn]).first()
    if (existing) {
      const willBeChecked = Boolean(existing.deletedAt)
      await db.habitCheckins.update(existing.id, { deletedAt: willBeChecked ? null : now, updatedAt: now })
      return willBeChecked
    }
    await db.habitCheckins.add({
      id: createId(),
      habitId,
      occurredOn,
      createdAt: now,
      updatedAt: now,
      deletedAt: null
    })
    return true
  }
}
